import React, { useEffect } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Table } from 'semantic-ui-react'
import { KeyLabelType } from '~/types'
import { splitEvery } from '~/utils/helpers'
import CalfRearingCell from './CalfRearingCell'
import { StyledTable } from './style'

const MAX_NUMBER_OF_COLUMNS = 7

interface Props {
  calfRearingTypes: KeyLabelType[]
}

const TableRearing: React.FC<Props> = ({ calfRearingTypes }) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const watchWeaningWeeks = useWatch({ control, name: 'weaningWeeks' })

  const { fields, replace, update } = useFieldArray({
    control,
    name: 'weaningWeeksValues',
    keyName: 'key'
  })

  useEffect(() => {
    if (fields.length < watchWeaningWeeks) {
      const newItems = []
      for (let i = fields.length; i < watchWeaningWeeks; i++) {
        newItems.push({
          calfRearing: '',
          order: i + 1
        })
      }
      replace([...fields, ...newItems])
      return
    } else if (fields.length > watchWeaningWeeks) {
      const newFields = [...fields].slice(0, watchWeaningWeeks)
      replace(newFields)
    }
  }, [watchWeaningWeeks])

  const calfRearingArrays = splitEvery(fields, MAX_NUMBER_OF_COLUMNS)

  function renderHeaders(headers, rowIndex) {
    return headers.map((header, headerIndex) => {
      return <Table.HeaderCell
        textAlign='center'
        key={header.key}
      >
        {t('{{weekNumber}}. Week', { weekNumber: headerIndex + MAX_NUMBER_OF_COLUMNS * rowIndex + 1 })}
      </Table.HeaderCell>
    })
  }

  function renderTableBody(calfRearingRow, rowIndex) {
    return calfRearingTypes?.map(calfingType => {
      return <Table.Row key={calfingType.key}>
        <Table.Cell textAlign={'center'}>{calfingType?.label}</Table.Cell>
        {calfRearingRow.map((calfRearingItem, index) => {
          const indexInArray = index + MAX_NUMBER_OF_COLUMNS * rowIndex
          return <Table.Cell
            textAlign={'center'}
            key={calfRearingItem.key} id={`calf-rearing-cell-${rowIndex}`}
          >
            <CalfRearingCell
              calfType={calfingType}
              update={(_, value) => update(indexInArray, { ...fields[indexInArray], calfRearing: value })
              }
              index={indexInArray}
              calfRearing={calfRearingItem}
            />
          </Table.Cell>
        })}
      </Table.Row>
    })
  }

  function renderCalfRearingWeeks() {
    const firstRowEl = document.getElementById('calf-rearing-cell-0') as HTMLTableRowElement
    return calfRearingArrays.map((calfRearingRow, rowIndex) => {
      return <StyledTable
        celled
        key={`rowIndex${rowIndex}`}
        style={{ width: firstRowEl && rowIndex > 0 ? firstRowEl.offsetWidth * (calfRearingRow.length + 1) : '' }}
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell textAlign='center' style={{ fontWeight: 'bold' }}>{t('Weeks')}</Table.HeaderCell>
            {renderHeaders(calfRearingRow, rowIndex)}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {renderTableBody(calfRearingRow, rowIndex)}
        </Table.Body>
      </StyledTable>
    })
  }

  return <div>
    {renderCalfRearingWeeks()}
  </div>
}
export default TableRearing